import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { BillingFeature, BoardPopupShareTab } from 'webapp/enums/enums';

import eq from 'webapp/helpers/eq';
import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import BoardsShareLinkTabComponent from 'webapp/components/boards/share/link-tab';
import BoardsShareSendTabComponent from 'webapp/components/boards/share/send-tab';
import BoardsShareStreamToTvTabComponent from 'webapp/components/boards/share/stream-to-tv-tab';
import BoardsShareUserAccessTabComponent from 'webapp/components/boards/share/user-access-tab';
import CommonShareEmbedComponent from 'webapp/components/common/share/embed';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

import type Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';

interface ShareTabItem {
	id: BoardPopupShareTab;
	text: string;
	locked?: boolean;
}

export default class BoardPopupShareComponent extends PopupInnerModalComponent<PopupData.Board.Share> {
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('billing') declare billingService: Service.Billing;

	@tracked selectedTab: BoardPopupShareTab | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// only set to first tab when we haven't set the tab explicitly on popup init
		let tabId = this.popupData.selectedTab;
		const targetTab = tabId ? this.getTab(tabId) : this.shownTabs[0];
		if (!targetTab || targetTab.locked) {
			const availableTab = this.shownTabs.find((t) => !t.locked);
			tabId = availableTab?.id;
		} else {
			tabId = targetTab.id;
		}

		if (tabId) {
			this.openTab(tabId);
		} else {
			this.showSharingLimitationPopup();
			void this.args.model.close(); // no tabs anyways
		}
	}

	get showUserAccessTab(): boolean {
		if (this.isBoardLoop) {
			return false;
		}
		return this.modelAsBoard.canEditPermissions;
	}

	get isUserAccessTab(): boolean {
		return this.selectedTab === BoardPopupShareTab.USER_ACCESS;
	}

	get isShareableLinkTab(): boolean {
		return this.selectedTab === BoardPopupShareTab.LINK;
	}

	get isSendTab(): boolean {
		return this.selectedTab === BoardPopupShareTab.SEND;
	}

	get isEmbedTab(): boolean {
		return this.selectedTab === BoardPopupShareTab.EMBED;
	}

	get isStreamToTVTab(): boolean {
		return this.selectedTab === BoardPopupShareTab.TV;
	}

	get model(): Board | BoardLoop {
		return this.popupData.model;
	}

	get modelAsBoard(): Board {
		return this.model as Board;
	}

	get isSharingTabsLocked() {
		return !this.billingService.sharingEnabled;
	}

	@cached
	get titlePreviewData() {
		const model = this.model;
		const titlePreviewData = {
			image: model.image,
			fallbackImage: '',
			color: model.color,
			boardCount: 0,
		};
		if (model instanceof BoardLoop) {
			titlePreviewData.boardCount = model.boardCount;
		}
		return titlePreviewData;
	}

	get isBoardLoop(): boolean {
		return this.model instanceof BoardLoop;
	}

	get modalTitle(): string {
		return `Share '${this.isBoardLoop ? (this.model as BoardLoop).title : (this.model as Board).name}'`;
	}

	get shownTabs(): ShareTabItem[] {
		const canEdit = this.model?.canCurrentUserEdit,
			isSharingLocked = this.isSharingTabsLocked,
			tabs: ShareTabItem[] = [];

		if (this.showUserAccessTab) {
			tabs.push({
				id: BoardPopupShareTab.USER_ACCESS,
				text: 'User access',
			});
		}

		tabs.push({
			id: BoardPopupShareTab.LINK,
			text: 'Shareable link',
			locked: isSharingLocked,
		});

		if (canEdit) {
			tabs.push({
				id: BoardPopupShareTab.SEND,
				text: 'Send snapshot',
				locked: isSharingLocked,
			});
		}

		if (this.model.isPublicEnabled || !this.currentUserService.isViewer) {
			tabs.push({
				id: BoardPopupShareTab.EMBED,
				text: 'Embed',
				locked: isSharingLocked,
			});

			tabs.push({
				id: BoardPopupShareTab.TV,
				text: 'Stream to TV',
				locked: isSharingLocked,
			});
		}

		return tabs;
	}

	private getTab(tabId: BoardPopupShareTab) {
		return this.shownTabs.find((tab) => tab.id === tabId);
	}

	private showSharingLimitationPopup() {
		this.billingService.handleLimitations(BillingFeature.SHARING);
	}

	@action
	openTab(tabId: BoardPopupShareTab): void {
		const targetTab = this.getTab(tabId);
		if (targetTab?.locked) {
			this.showSharingLimitationPopup();
			return;
		}
		this.selectedTab = tabId;
		this.scheduleReposition();
	}

	@action
	openLinkTab() {
		this.openTab(BoardPopupShareTab.LINK);
	}
	<template>
		<div class="title">
			<BoardImagePreviewComponent
				@image={{this.titlePreviewData.image}}
				@fallbackImage={{this.titlePreviewData.fallbackImage}}
				@color={{this.titlePreviewData.color}}
				@count={{this.titlePreviewData.boardCount}}
			/>
			<span>{{this.modalTitle}}</span>
		</div>

		<ul class="modalTabs modalTabs-2">
			{{#each this.shownTabs as |tab|}}
				<li
					class="navigation-button
						{{if (eq this.selectedTab tab.id) 'navigation-button--selected'}}"
				>
					<a role="button" {{on "click" (fn this.openTab tab.id)}}>
						{{tab.text}}
						{{#if tab.locked}}
							<CommonUiIconComponent @name="Lock" @size="sm" />
						{{/if}}
					</a>
				</li>
			{{/each}}
		</ul>

		<div class="content">

			{{#if this.showUserAccessTab}}
				<BoardsShareUserAccessTabComponent
					@model={{this.modelAsBoard}}
					@isContentShown={{this.isUserAccessTab}}
					@trackingOrigin={{this.popupData.trackingOrigin}}
					@onContentChange={{this.scheduleReposition}}
					@onClose={{this.close}}
				/>
			{{/if}}

			<BoardsShareLinkTabComponent
				@model={{this.model}}
				@previewHash={{this.popupData.previewHash}}
				@isContentShown={{this.isShareableLinkTab}}
				@trackingOrigin={{this.popupData.trackingOrigin}}
				@onContentChange={{this.scheduleReposition}}
				@onClose={{this.close}}
			/>

			<BoardsShareSendTabComponent
				@model={{this.model}}
				@isContentShown={{this.isSendTab}}
				@trackingOrigin={{this.popupData.trackingOrigin}}
				@onContentChange={{this.scheduleReposition}}
				@onClose={{this.close}}
			/>

			<CommonShareEmbedComponent
				@model={{this.model}}
				@previewHash={{this.popupData.previewHash}}
				@isContentShown={{this.isEmbedTab}}
				@openLinkTab={{this.openLinkTab}}
				@onClose={{this.close}}
			/>

			<BoardsShareStreamToTvTabComponent
				@model={{this.model}}
				@previewHash={{this.popupData.previewHash}}
				@isContentShown={{this.isStreamToTVTab}}
				@openLinkTab={{this.openLinkTab}}
				@onClose={{this.close}}
			/>

		</div>

		<div class="filler"></div>
	</template>
}
